<template>
  <div class="timeline" :class="{ vertical: orientation === 'vertical' }">
    <!-- Dates -->
    <ul class="dates">
      <li v-for="(item, index) in events" :key="index">
        <a href="#"
           :class="{ selected: index === currentIndex }"
           @click.prevent="goTo(index)">
          {{ item.year }}
        </a>
      </li>
    </ul>

    <!-- Issues / Content -->
    <ul class="issues" :style="issuesStyle">
      <li v-for="(item, index) in events" :key="index"
          :class="{ selected: index === currentIndex }">
        <h1>{{ item.year }}</h1>
        <p>{{ item.description }}</p>
      </li>
    </ul>

    <!-- Navigation -->
    <button class="prev" @click="prev" :disabled="currentIndex === 0">‹</button>
    <button class="next" @click="next" :disabled="currentIndex === events.length - 1">›</button>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';

// Define props
const props = defineProps({
  events: {
    type: Array,
    required: true,
    default: () => []
  },
  orientation: {
    type: String,
    default: 'horizontal' // 'horizontal' or 'vertical'
  },
  autoPlay: {
    type: Boolean,
    default: false
  },
  autoPlayDirection: {
    type: String,
    default: 'forward' // 'forward' or 'backward'
  },
  autoPlayInterval: {
    type: Number,
    default: 3000 // ms
  },
  arrowKeys: {
    type: Boolean,
    default: true
  }
});

// State
const currentIndex = ref(0);
let intervalId = null;

// Computed styles for sliding issues
const issuesStyle = computed(() => {
  return props.orientation === 'horizontal'
    ? { marginLeft: `-${currentIndex.value * 100}%` }
    : { marginTop: `-${currentIndex.value * 100}%` };
});

// Methods
const goTo = (index) => currentIndex.value = index;
const next = () => currentIndex.value = (currentIndex.value + 1) % props.events.length;
const prev = () => currentIndex.value = (currentIndex.value - 1 + props.events.length) % props.events.length;

// Autoplay
const startAutoPlay = () => {
  if (props.autoPlay) {
    intervalId = setInterval(() => {
      props.autoPlayDirection === 'forward' ? next() : prev();
    }, props.autoPlayInterval);
  }
};
const stopAutoPlay = () => intervalId && clearInterval(intervalId);

// Arrow key navigation
const handleKeydown = (e) => {
  if (!props.arrowKeys) return;
  if (props.orientation === 'horizontal') {
    if (e.key === 'ArrowRight') next();
    if (e.key === 'ArrowLeft') prev();
  } else {
    if (e.key === 'ArrowDown') next();
    if (e.key === 'ArrowUp') prev();
  }
};

onMounted(() => {
  startAutoPlay();
  window.addEventListener('keydown', handleKeydown);
});

onBeforeUnmount(() => {
  stopAutoPlay();
  window.removeEventListener('keydown', handleKeydown);
});
</script>


<style scoped>
.timeline {
  width: 100%;
  max-width: 1400px;
  margin: 0px auto;
  position: relative;
  overflow: hidden;
  background: #343a40;
  border-radius: 12px;
  color: #fff;
}
.timeline.vertical .issues {
  flex-direction: column;
}

.dates {
  display: flex;
  justify-content: center;
  gap: 15px;
  padding: 15px;
  background: #222;
  border-bottom: 3px solid #c39738;
}

.dates li a {
  color: #fff;
  text-decoration: none;
  font-weight: 600;
  transition: color 0.3s;
}

.dates li a.selected {
  font-size: 1.15rem;
  color: #c39738;
  border-bottom: 2px solid #c39738;
  padding-bottom: 3px;
}

.issues {
  display: flex;
  width: 100%;
  transition: margin 0.5s ease;
}

.issues li {
  width: 100%;
  flex-shrink: 0;
  padding: 30px 100px;
  box-sizing: border-box;
  text-align: center;
}

.issues li h1 {
  font-size: 1.8rem;
  margin-bottom: 10px;
  color: #c39738;
}

.issues li p {
  font-size: 1.1rem;
  line-height: 1.5;
  color: #f1f1f1;
}

button.prev,
button.next {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 50px;
  height: 50px;
  border-radius: 50%;
  border: none;
  background: #c39738;
  color: #343a40;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1.5rem;
  transition: background 0.3s, box-shadow 0.3s;
}

button.prev {
  left: 10px;
}

button.next {
  right: 10px;
}

button:hover:not(:disabled) {
  background: #fff;
  box-shadow: 0 0 12px rgba(195, 151, 56, 0.8);
}

button:disabled {
  opacity: 0.4;
  cursor: default;
}

</style>
